import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, BarChart3, BookOpen, Boxes, Briefcase, Building2, CalendarClock, CalendarDays, CircleDollarSign, ClipboardCheck, ClipboardList, Clock, Factory, FileText, FolderKanban, Globe, LayoutGrid, Activity, Gauge, HeartPulse, Bell, HelpCircle, Inbox, KeyRound, LayoutDashboard, LayoutTemplate, LifeBuoy, ListChecks, LockKeyhole, LogOut, MailQuestion, MapPin, MessageSquare, Moon, Network, Palette, PhoneCall, PlusCircle, RadioTower, RefreshCw, Settings, ShieldCheck, Sparkles, Ticket, Truck, Upload, Users, Warehouse, Webhook, Wrench, Zap, ChevronDown, Languages, Menu, X, UserRound, } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BrandMark } from '@/components/BrandMark';
import { GlobalShortcuts } from '@/components/GlobalShortcuts';
import { SessionIndicator } from '@/components/SessionIndicator';
import { signOutEverywhere } from '@/lib/authActions';
import { stopImpersonation } from '@/lib/impersonation';
import { useIdleTimeout } from '@/lib/idleTimeout';
import { ProductWordmark } from '@/components/ProductWordmark';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { NotificationBell } from '@/components/NotificationBell';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { ThemeToggle } from '@/components/ThemeToggle';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { applyLocale, applyLocaleForMe, MULTI_LANGUAGE_FLAG } from '@/lib/i18n';
import { LANGUAGES } from '@/lib/languages';
import { pca } from '@/lib/msal';
import { hasPerm, resolvePersona, useAuthStore, } from '@/store/auth';
// Slice-3 sidebar — purely permission-driven.
//
// EVERY item declares the single permission key the user must hold to see
// it. The sidebar derives nothing from role names. When the RBAC matrix
// admin grants the user a new permission via /admin/rbac, the next /me
// refresh adds that key to me.permissions and the corresponding sidebar
// item appears automatically — no role-tier gating, no hard-coded
// "if isAdmin" branches.
//
// Section headings (Administration / Self-service) are NOT hard-coded
// to a tier either — the render layer hides any section whose item list
// is empty after permission filtering, so the heading appears or
// disappears in lockstep with the items beneath it.
// Slice-3 sidebar regrouped into labelled domain sections (2026-06-04 UX pass).
// PURE reorganisation: every item keeps its exact `to` route, `perm` key, label
// and icon — only the grouping changed. Headed sections render collapsible (see
// the render layer); the un-headed top cluster is always visible. The perm
// filter + empty-section-hiding behaviour is unchanged.
const adminLayout = [
    // Platform-owner section — only the Super Admin holds `platform.admin`,
    // so this whole section is hidden for Tenant Admins and below (the
    // render layer drops a section whose items all fail the perm filter).
    {
        heading: 'Platform',
        items: [
            { to: '/admin/settings', label: 'Settings', icon: _jsx(Settings, { size: 16 }), perm: 'tenant.update' },
            { to: '/admin/tenants', label: 'Tenants', icon: _jsx(Boxes, { size: 16 }), perm: 'platform.admin' },
            { to: '/admin/platform-insights', label: 'Azure Analysis', icon: _jsx(Gauge, { size: 16 }), perm: 'platform.ops.read' },
        ],
    },
    // Top cluster — the daily-driver entry points. No heading → always visible
    // (not collapsible), so the most-used links never hide behind a chevron.
    {
        items: [
            { to: '/', label: 'Home', icon: _jsx(LayoutDashboard, { size: 16 }) },
            // "My Queue" is a saved-view link (assignee=me) so any holder of
            // tickets.view sees it; the URL is parsed by the Tickets list.
            { to: '/tickets?assignee=me', label: 'My Queue', icon: _jsx(Inbox, { size: 16 }), perm: 'tickets.view' },
            { to: '/tickets', label: 'Tickets', icon: _jsx(Ticket, { size: 16 }), perm: 'tickets.view' },
            // One-click "everything clients raised via the portal" view (Zendesk/
            // Freshdesk shared-view parity) — all statuses, unassigned included.
            { to: '/tickets?channel=portal', label: 'Client Tickets', icon: _jsx(Globe, { size: 16 }), perm: 'tickets.view' },
            { to: '/tickets/new', label: 'Create Ticket', icon: _jsx(PlusCircle, { size: 16 }), perm: 'tickets.create' },
            { to: '/approvals', label: 'Approvals', icon: _jsx(ClipboardCheck, { size: 16 }), perm: 'approval.read' },
            // Team Chat (Phase A, 2026-08-14) — internal staff messaging with
            // @mentions. Permission-gated; customers/vendors never hold it.
            { to: '/team-chat', label: 'Team Chat', icon: _jsx(MessageSquare, { size: 16 }), perm: 'teamchat.use' },
            // Notifications is always visible — backend scopes rows to the
            // authenticated user, so no permission key gates it.
            { to: '/notifications', label: 'Notifications', icon: _jsx(Bell, { size: 16 }) },
        ],
    },
    {
        heading: 'Field Service',
        items: [
            // Field engineers' personal schedule + worklog.
            { to: '/me/today', label: 'My Day', icon: _jsx(CalendarDays, { size: 16 }), perm: 'workorder.read', flag: 'field_service_module' },
            { to: '/me/time', label: 'My Time', icon: _jsx(Clock, { size: 16 }), perm: 'time.log' },
            { to: '/dispatch', label: 'Dispatch Board', icon: _jsx(LayoutGrid, { size: 16 }), perm: 'workorder.read', flag: 'field_service_module' },
            { to: '/dispatch/map', label: 'Dispatch Map', icon: _jsx(MapPin, { size: 16 }), perm: 'workorder.read', flag: 'field_service_module' },
            { to: '/work-orders', label: 'Work Orders', icon: _jsx(ClipboardList, { size: 16 }), perm: 'workorder.read', flag: 'field_service_module' },
            { to: '/admin/appointment-slots', label: 'Appointment Slots', icon: _jsx(CalendarClock, { size: 16 }), perm: 'workorder.manage', flag: 'field_service_module' },
            { to: '/admin/recurring-maintenance', label: 'Recurring Maintenance', icon: _jsx(CalendarClock, { size: 16 }), perm: 'workorder.manage', flag: 'field_service_module' },
            { to: '/admin/timesheets', label: 'Timesheets', icon: _jsx(ClipboardCheck, { size: 16 }), perm: 'time.read' },
            { to: '/admin/ack-reviews', label: 'Force-close Reviews', icon: _jsx(ClipboardCheck, { size: 16 }), perm: 'workorder.manage', flag: 'field_service_module' },
            { to: '/admin/visit-extension-disputes', label: 'Extension Disputes', icon: _jsx(AlertTriangle, { size: 16 }), perm: 'visit.extension.dispute', flag: 'field_service_module' },
        ],
    },
    {
        // Split 2026-07-30 (owner-approved): customer master data and the Assets/
        // CMDB suite were one 17-item group, burying Services/Vendors/Freelancers
        // below the asset pages. Now two focused sections; the Assets & CMDB
        // section vanishes entirely when the assets_cmdb flag is off (empty
        // sections are filtered before render).
        heading: 'Customers & Catalog',
        items: [
            { to: '/admin/clients', label: 'Clients', icon: _jsx(Building2, { size: 16 }), perm: 'clients.view' },
            { to: '/admin/sites', label: 'Sites', icon: _jsx(MapPin, { size: 16 }), perm: 'site.read' },
            { to: '/admin/services', label: 'Services', icon: _jsx(ListChecks, { size: 16 }), perm: 'service.read' },
            { to: '/admin/vendors', label: 'Vendors', icon: _jsx(Truck, { size: 16 }), perm: 'vendor.read', flag: 'field_service_module' },
            { to: '/admin/freelancers', label: 'Freelancers', icon: _jsx(UserRound, { size: 16 }), perm: 'freelancer.read', flag: 'field_service_module' },
            // Sub-Projects are nested inside each Project (expandable rows + the
            // "+ Add sub-project" action), so there's no separate nav entry; the
            // flat-list page at /admin/sub-projects stays reachable by direct link.
            { to: '/admin/projects', label: 'Projects', icon: _jsx(FolderKanban, { size: 16 }), perm: 'project.read' },
            { to: '/cs/health', label: 'Customer Success', icon: _jsx(HeartPulse, { size: 16 }), perm: 'customer_success.read' },
            { to: '/admin/import', label: 'Bulk Import', icon: _jsx(Upload, { size: 16 }), perm: 'client.create' },
        ],
    },
    {
        heading: 'Assets & CMDB',
        items: [
            { to: '/admin/assets', label: 'Assets', icon: _jsx(Boxes, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
            { to: '/admin/assets/catalog', label: 'Manufacturers & Models', icon: _jsx(Factory, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
            { to: '/admin/sam', label: 'Software (SAM)', icon: _jsx(ShieldCheck, { size: 16 }), perm: 'sam.read', flag: 'assets_cmdb' },
            { to: '/admin/cmdb/discovery', label: 'Discovery', icon: _jsx(RadioTower, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
            { to: '/admin/cmdb/health', label: 'CMDB Health', icon: _jsx(Gauge, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
            { to: '/admin/cmdb/stockroom', label: 'Stockroom', icon: _jsx(Warehouse, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
            { to: '/admin/cmdb/procurement', label: 'Procurement & Audit', icon: _jsx(ClipboardList, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
            { to: '/admin/cmdb/financials', label: 'Asset Financials', icon: _jsx(CircleDollarSign, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
            { to: '/admin/cmdb/services', label: 'Business Services', icon: _jsx(Network, { size: 16 }), perm: 'asset.read', flag: 'assets_cmdb' },
        ],
    },
    {
        heading: 'Knowledge',
        items: [
            { to: '/kb', label: 'Knowledge Base', icon: _jsx(BookOpen, { size: 16 }), perm: 'kb.read' },
            { to: '/kb/decision-trees', label: 'Guided Resolutions', icon: _jsx(BookOpen, { size: 16 }), perm: 'kb.read' },
            { to: '/kb/analytics', label: 'Knowledge Analytics', icon: _jsx(BookOpen, { size: 16 }), perm: 'kb.read' },
        ],
    },
    {
        heading: 'Insights',
        items: [
            { to: '/reports', label: 'Reports', icon: _jsx(BarChart3, { size: 16 }), perm: 'reports.view' },
            { to: '/reports/builder', label: 'Report Builder', icon: _jsx(BarChart3, { size: 16 }), perm: 'reports.view' },
            { to: '/reports/dashboards', label: 'Dashboards', icon: _jsx(BarChart3, { size: 16 }), perm: 'reports.view' },
            { to: '/reports/schedules', label: 'Scheduled Reports', icon: _jsx(BarChart3, { size: 16 }), perm: 'reports.view' },
            { to: '/reports/predictions', label: 'Predictions', icon: _jsx(BarChart3, { size: 16 }), perm: 'reports.view' },
            { to: '/reports/ai-value', label: 'AI / Ops Value', icon: _jsx(Sparkles, { size: 16 }), perm: 'reports.view' },
        ],
    },
    {
        heading: 'SLA & Escalations',
        items: [
            { to: '/admin/sla', label: 'SLA Policies', icon: _jsx(ShieldCheck, { size: 16 }), perm: 'sla_policies.view' },
            { to: '/admin/holiday-calendars', label: 'Holiday Calendars', icon: _jsx(CalendarDays, { size: 16 }), perm: 'sla.read' },
            { to: '/admin/rate-cards', label: 'Rate Cards', icon: _jsx(CircleDollarSign, { size: 16 }), perm: 'rate_card.read' },
            { to: '/admin/escalations', label: 'Escalations', icon: _jsx(AlertTriangle, { size: 16 }), perm: 'escalation.read' },
            { to: '/admin/on-call', label: 'On-Call Schedules', icon: _jsx(PhoneCall, { size: 16 }), perm: 'oncall.read' },
        ],
    },
    {
        heading: 'Channels & Feedback',
        items: [
            { to: '/admin/channels', label: 'Channels', icon: _jsx(Inbox, { size: 16 }), perm: 'channel.manage' },
            { to: '/chat', label: 'Chat Console', icon: _jsx(MessageSquare, { size: 16 }), perm: 'chat.handle' },
            { to: '/admin/sms-config', label: 'SMS (Twilio)', icon: _jsx(MessageSquare, { size: 16 }), perm: 'tenant.sms.manage' },
            { to: '/admin/email-branding', label: 'Email Branding', icon: _jsx(Palette, { size: 16 }), perm: 'tenant.update' },
            { to: '/admin/email-templates', label: 'Email Templates', icon: _jsx(FileText, { size: 16 }), perm: 'tenant.update' },
            { to: '/admin/notification-matrix', label: 'Notification Matrix', icon: _jsx(LayoutGrid, { size: 16 }), perm: 'notification.read' },
            { to: '/admin/email-inbox', label: 'Email Inbox', icon: _jsx(MailQuestion, { size: 16 }), perm: 'email_inbox.read' },
            { to: '/admin/status-page', label: 'Status Page', icon: _jsx(Activity, { size: 16 }), perm: 'status_page.manage' },
            { to: '/admin/csat', label: 'CSAT Responses', icon: _jsx(ClipboardCheck, { size: 16 }), perm: 'csat.read' },
        ],
    },
    {
        heading: 'Configuration',
        items: [
            { to: '/admin/automation', label: 'Automation', icon: _jsx(Zap, { size: 16 }), perm: 'automation.read' },
            { to: '/admin/monitoring', label: 'Monitoring / AIOps', icon: _jsx(Activity, { size: 16 }), perm: 'monitoring.read', flag: 'assets_cmdb' },
            { to: '/admin/monitoring/runbooks', label: 'Remediation Runbooks', icon: _jsx(ShieldCheck, { size: 16 }), perm: 'monitoring.read', flag: 'assets_cmdb' },
            { to: '/admin/macros', label: 'Macros', icon: _jsx(Zap, { size: 16 }), perm: 'macro.manage' },
            { to: '/admin/ticket-templates', label: 'Ticket Templates', icon: _jsx(FileText, { size: 16 }), perm: 'ticket_template.manage' },
            { to: '/admin/ticket-categories', label: 'Ticket Categories', icon: _jsx(Settings, { size: 16 }), perm: 'taxonomy.manage' },
            { to: '/admin/custom-fields', label: 'Custom Fields', icon: _jsx(Settings, { size: 16 }), perm: 'custom_field.read' },
            { to: '/admin/form-templates', label: 'Form Designer', icon: _jsx(LayoutTemplate, { size: 16 }), perm: 'form_template.read' },
        ],
    },
    {
        heading: 'People & Access',
        items: [
            { to: '/admin/users', label: 'User Management', icon: _jsx(Users, { size: 16 }), perm: 'users.view' },
            { to: '/admin/groups', label: 'Groups & Queues', icon: _jsx(Users, { size: 16 }), perm: 'group.read' },
            // RBAC Insights — gated by `users.edit` (the sentinel "can manage roles"
            // permission the Last-Admin Lock already protects).
            { to: '/admin/rbac', label: 'RBAC Insights', icon: _jsx(LockKeyhole, { size: 16 }), perm: 'users.edit' },
            { to: '/admin/identity', label: 'SSO & SCIM', icon: _jsx(KeyRound, { size: 16 }), perm: 'identity.manage' },
        ],
    },
    {
        heading: 'Integrations & Developer',
        items: [
            { to: '/admin/api-keys', label: 'API Keys', icon: _jsx(KeyRound, { size: 16 }), perm: 'api_key.manage' },
            { to: '/admin/webhooks', label: 'Webhooks', icon: _jsx(Webhook, { size: 16 }), perm: 'webhook.read' },
            { to: '/admin/developer', label: 'Developer & Integrity', icon: _jsx(Webhook, { size: 16 }), perm: 'api_key.manage' },
        ],
    },
    {
        heading: 'Billing & Contracts',
        items: [
            { to: '/admin/contracts', label: 'Contracts', icon: _jsx(FileText, { size: 16 }), perm: 'contract.read' },
            { to: '/admin/billing', label: 'Plan & Usage', icon: _jsx(Gauge, { size: 16 }), perm: 'subscription.manage' },
        ],
    },
    {
        heading: 'Audit & Logs',
        items: [
            { to: '/admin/audit', label: 'Audit Log', icon: _jsx(FileText, { size: 16 }), perm: 'audit.read' },
            { to: '/admin/notification-log', label: 'Notification Log', icon: _jsx(Bell, { size: 16 }), perm: 'audit.read' },
        ],
    },
    // Personal preferences. Notifications is open to all; Quiet Hours is gated
    // by device.register (per-item perm).
    {
        heading: 'My preferences',
        items: [
            { to: '/me/notifications', label: 'Notifications', icon: _jsx(Bell, { size: 16 }) },
            { to: '/me/quiet-hours', label: 'Quiet Hours', icon: _jsx(Moon, { size: 16 }), perm: 'device.register' },
        ],
    },
    // Help is always visible — article-level audience filtering happens inside
    // the page based on resolvePersona(me).
    {
        heading: 'Help',
        items: [
            { to: '/help', label: 'Help & Docs', icon: _jsx(LifeBuoy, { size: 16 }) },
            { to: '/help/glossary', label: 'Glossary', icon: _jsx(HelpCircle, { size: 16 }) },
        ],
    },
];
const customerLayout = [
    {
        heading: 'Self-service',
        items: [
            { to: '/portal', label: 'Portal Home', icon: _jsx(Globe, { size: 16 }) },
            { to: '/portal/tickets', label: 'My Tickets', icon: _jsx(Inbox, { size: 16 }) },
            { to: '/portal/approvals', label: 'Approvals', icon: _jsx(ClipboardCheck, { size: 16 }), perm: 'approval.read' },
            { to: '/portal/tickets/new', label: 'Create Ticket', icon: _jsx(Ticket, { size: 16 }) },
            { to: '/portal/appointments', label: 'Book appointment', icon: _jsx(CalendarClock, { size: 16 }) },
            { to: '/portal/kb', label: 'Knowledge Base', icon: _jsx(BookOpen, { size: 16 }) },
            // Phase 3 — live chat was built (routed at /portal/chat) but never linked.
            { to: '/portal/chat', label: 'Live Chat', icon: _jsx(MessageSquare, { size: 16 }) },
            { to: '/notifications', label: 'Notifications', icon: _jsx(Bell, { size: 16 }) },
        ],
    },
    {
        heading: 'My preferences',
        items: [
            { to: '/me/notifications', label: 'Notifications', icon: _jsx(Bell, { size: 16 }) },
            { to: '/me/quiet-hours', label: 'Quiet Hours', icon: _jsx(Moon, { size: 16 }), perm: 'device.register' },
        ],
    },
    {
        heading: 'Help',
        items: [
            { to: '/help', label: 'Help & Docs', icon: _jsx(LifeBuoy, { size: 16 }) },
            { to: '/help/glossary', label: 'Glossary', icon: _jsx(HelpCircle, { size: 16 }) },
        ],
    },
];
const vendorLayout = [
    {
        heading: 'Vendor portal',
        items: [
            // M3 — the portal core: company dashboard + the real dispatched-jobs
            // queue (WorkOrder.vendor_id). "My Work Queue" (ticket-assignment-based,
            // usually empty for vendors) is retired from the nav; its route stays
            // alive for old links.
            { to: '/vendor/dashboard', label: 'Dashboard', icon: _jsx(Wrench, { size: 16 }), flag: 'vendor_portal' },
            { to: '/vendor/jobs', label: 'My Jobs', icon: _jsx(ClipboardList, { size: 16 }), flag: 'vendor_portal' },
            // M4 — the company's saved field-engineer roster (feeds Assign engineer).
            { to: '/vendor/engineers', label: 'My Engineers', icon: _jsx(Users, { size: 16 }), flag: 'vendor_portal' },
            // M5 — profile self-service + shared scorecards + compliance documents.
            { to: '/vendor/company', label: 'My Company', icon: _jsx(Building2, { size: 16 }), flag: 'vendor_portal' },
            { to: '/vendor', label: 'Assigned Tickets', icon: _jsx(Briefcase, { size: 16 }) },
            // RFQ inbox is an RFQ-flag surface — hidden for tenants that only run
            // the vendor portal without competitive bidding (M1 0.1).
            { to: '/vendor/rfq', label: 'Quote Requests', icon: _jsx(MailQuestion, { size: 16 }), flag: 'phase2_vendor_rfq' },
            { to: '/notifications', label: 'Notifications', icon: _jsx(Bell, { size: 16 }) },
        ],
    },
    {
        heading: 'My preferences',
        items: [
            { to: '/me/notifications', label: 'Notifications', icon: _jsx(Bell, { size: 16 }) },
            { to: '/me/quiet-hours', label: 'Quiet Hours', icon: _jsx(Moon, { size: 16 }), perm: 'device.register' },
        ],
    },
    {
        heading: 'Help',
        items: [
            { to: '/help', label: 'Help & Docs', icon: _jsx(LifeBuoy, { size: 16 }) },
            { to: '/help/glossary', label: 'Glossary', icon: _jsx(HelpCircle, { size: 16 }) },
        ],
    },
];
function navFor(me) {
    // Layout selection is by URL space, not by capability — a Customer
    // belongs in /portal, a Vendor in /vendor, internal staff in the main
    // dashboard. Within the chosen layout, item visibility is governed
    // strictly by `perm` keys on each item (no role-name gating).
    const persona = resolvePersona(me);
    if (persona === 'customer')
        return customerLayout;
    if (persona === 'vendor')
        return vendorLayout;
    return adminLayout;
}
function personaBadge(me) {
    const p = resolvePersona(me);
    switch (p) {
        case 'super_admin': return { label: 'Super Admin', tone: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300' };
        case 'admin': return { label: 'Admin', tone: 'bg-brand-100 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300' };
        case 'vendor': return { label: 'Vendor', tone: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' };
        case 'customer': return { label: 'Customer', tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' };
        default: return { label: 'Agent', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300' };
    }
}
// Idle-timeout windows. Industry-standard for SaaS admin dashboards
// (Microsoft 365 admin, Google Workspace, Stripe): 30 minutes of
// inactivity logs the user out. We surface a toast at the 25-minute
// mark so users can move their mouse / hit a key to stay signed in
// rather than being kicked silently. Both values are exported so the
// Login page can word the post-timeout banner with the same number.
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_WARN_BEFORE_MS = 5 * 60 * 1000;
// Per-group collapse state is remembered across sessions (display-only).
const NAV_EXPANDED_KEY = 'styx.nav.expanded';
function loadExpandedGroups() {
    try {
        const raw = localStorage.getItem(NAV_EXPANDED_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
    }
    catch {
        return {};
    }
}
/** Unread Team-Chat messages — a small lime chip on the sidebar item.
 *  Polls every 30s; renders nothing at zero. */
function TeamChatUnreadChip() {
    const unread = useQuery({
        queryKey: ['teamchat-unread'],
        queryFn: () => api.get('/team-chat/unread-count'),
        refetchInterval: 30_000,
        staleTime: 15_000,
    });
    const n = unread.data?.unread ?? 0;
    if (n <= 0)
        return null;
    return (_jsx("span", { className: "ml-auto rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold text-styx-900", children: n > 99 ? '99+' : n }));
}
export function AppShell() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const { devUser, setMe, logout } = useAuthStore();
    const toast = useToast();
    const [confirmSignOutAll, setConfirmSignOutAll] = useState(false);
    const [signingOutAll, setSigningOutAll] = useState(false);
    // Mobile slide-over drawer (≤ md). Desktop sidebar is always in-flow.
    const [sidebarOpen, setSidebarOpen] = useState(false);
    // Per-group collapse state (display-only), remembered across sessions.
    const [expandedGroups, setExpandedGroups] = useState(loadExpandedGroups);
    // In MSAL mode, the user has signed in but `devUser` is null — gate /me on
    // either credential being present. `msalSignedIn` flips to true once MSAL's
    // active-account is set (synchronously after initMsal()'s handleRedirect).
    const msalSignedIn = !!pca && !!pca.getActiveAccount();
    const hasCredential = !!devUser || msalSignedIn;
    // Query key includes both so a switch (dev → msal, or vice versa) re-fires.
    const queryIdentity = devUser ?? (msalSignedIn ? 'msal' : null);
    const meQuery = useQuery({
        queryKey: ['me', queryIdentity],
        queryFn: () => api.get('/me'),
        enabled: hasCredential,
        retry: false,
    });
    // Sync the freshly-fetched /me into the auth store. We sync ONLY when a
    // genuine /me fetch lands (dataUpdatedAt advances) — never on a plain
    // re-render — so manual setMe() calls elsewhere (feature-flag toggle, OOF
    // toggle, impersonation "View as") are never clobbered by a stale query
    // cache. We compare the fetched payload against the CURRENT store value to
    // skip needless writes/re-renders when a focus refetch returns identical
    // data. Previously this guarded on `user.id`, so a permission/role change
    // for the SAME user only took effect after a full sign-out/sign-in; now
    // access changes propagate within the session (TanStack refetches /me on
    // window focus + reconnect, see main.tsx defaults).
    const lastMeSyncedAt = useRef(0);
    useEffect(() => {
        if (!meQuery.data || meQuery.dataUpdatedAt <= lastMeSyncedAt.current)
            return;
        lastMeSyncedAt.current = meQuery.dataUpdatedAt;
        if (JSON.stringify(meQuery.data) !== JSON.stringify(useAuthStore.getState().me)) {
            setMe(meQuery.data);
        }
        // Interface language follows /me (multi_language flag + user locale).
        // Flag OFF → pins English, byte-identical to pre-i18n behavior.
        applyLocaleForMe(meQuery.data);
    }, [meQuery.data, meQuery.dataUpdatedAt, setMe]);
    // On reload, apply the persisted user's language immediately (before the
    // /me refetch lands) so the sidebar doesn't flash English first.
    useEffect(() => {
        applyLocaleForMe(useAuthStore.getState().me);
    }, []);
    // Close the mobile drawer whenever the route changes (a nav tap navigated).
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);
    // Idle-timeout: 30 minutes of no mouse/keyboard activity → sign out
    // with a clear reason on the Login page. Toast 5 minutes before so
    // users can move the mouse to stay in. Disabled when there's no
    // credential (we're about to redirect to /login anyway).
    useIdleTimeout({
        timeoutMs: IDLE_TIMEOUT_MS,
        warnAtMs: IDLE_WARN_BEFORE_MS,
        disabled: !hasCredential,
        onWarn: () => {
            toast.info('Session expiring soon', 'You\'ll be signed out in 5 minutes due to inactivity. Move your mouse to stay signed in.');
        },
        onTimeout: () => {
            logout();
            navigate('/login?reason=idle');
        },
    });
    if (!hasCredential) {
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    if (meQuery.isLoading) {
        return (_jsx("div", { className: "flex h-screen items-center justify-center text-sm text-slate-500", children: "Loading\u2026" }));
    }
    if (meQuery.isError) {
        const displayUser = devUser ?? pca?.getActiveAccount()?.username ?? 'user';
        return (_jsxs("div", { className: "flex h-screen flex-col items-center justify-center gap-4 p-6 text-sm", children: [_jsx(AlertCircle, { className: "h-12 w-12 text-red-500" }), _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "font-semibold", children: "Authentication failed" }), _jsxs("div", { className: "text-slate-500", children: ["User \u201C", displayUser, "\u201D is not provisioned in the backend."] }), _jsx("div", { className: "mt-2 text-xs text-slate-400", children: meQuery.error?.message ?? 'Unknown error' })] }), _jsx(Button, { onClick: () => { logout(); navigate('/login'); }, children: "Sign out" })] }));
    }
    const meData = meQuery.data;
    if (!meData)
        return null;
    const sections = navFor(meData);
    const badge = personaBadge(meData);
    // Apply the permission filter once per section in advance, so a section
    // whose items all fail their perm checks doesn't render its heading
    // (which would otherwise hover above an empty list).
    const visibleSections = sections
        .map((section) => ({
        ...section,
        items: section.items.filter((it) => (!it.perm || hasPerm(meData, it.perm)) &&
            (!it.flag || Boolean(meData?.feature_flags?.[it.flag]))),
    }))
        .filter((section) => section.items.length > 0);
    const renderItems = (items) => items.map((it) => (_jsxs(NavLink, { to: it.to, end: it.to === '/' || it.to === '/portal' || it.to === '/vendor', onClick: () => setSidebarOpen(false), className: ({ isActive }) => cn('flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors', 
        // Sidebar is always dark teal (styx-700) — active item gets
        // a styx-800 chip with brand-400 (lime) text so the highlight
        // pops against the teal background. Inactive items sit at
        // styx-200 (washed teal) and lighten on hover.
        isActive
            ? 'bg-styx-800 text-brand-400'
            : 'text-styx-200 hover:bg-styx-800/60 hover:text-white'), children: [it.icon, t(it.label), it.to === '/team-chat' && _jsx(TeamChatUnreadChip, {})] }, it.to)));
    // Group collapse helpers (display-only). A headed group is shown open when it
    // contains the active route (so the current page is never hidden) OR the user
    // has expanded it. Toggling persists to localStorage.
    const toggleGroup = (heading) => {
        setExpandedGroups((prev) => {
            const next = { ...prev, [heading]: !prev[heading] };
            try {
                localStorage.setItem(NAV_EXPANDED_KEY, JSON.stringify(next));
            }
            catch {
                /* ignore quota / privacy-mode failures */
            }
            return next;
        });
    };
    const pathMatches = (to) => {
        const path = to.split('?')[0];
        if (path === '/')
            return location.pathname === '/';
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };
    const groupIsActive = (items) => items.some((it) => pathMatches(it.to));
    return (_jsxs("div", { className: "flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950", children: [_jsx(GlobalShortcuts, {}), sidebarOpen && (_jsx("div", { className: "fixed inset-0 z-30 bg-black/40 md:hidden", "aria-hidden": true, onClick: () => setSidebarOpen(false) })), _jsxs("aside", { className: cn('fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-styx-800 bg-styx-700 text-styx-100 transition-transform md:static md:z-auto md:translate-x-0', sidebarOpen ? 'translate-x-0' : '-translate-x-full'), children: [_jsxs("div", { className: "relative flex h-20 flex-col items-start justify-center gap-1 border-b border-styx-800 px-5", children: [_jsx(BrandMark, { variant: "compact", onDark: true }), _jsx(ProductWordmark, { size: "sm" }), _jsx("button", { type: "button", "aria-label": "Close menu", onClick: () => setSidebarOpen(false), className: "absolute right-3 top-3 rounded-md p-1 text-styx-300 hover:bg-styx-800/70 hover:text-white md:hidden", children: _jsx(X, { size: 18 }) })] }), _jsx("nav", { className: "flex-1 space-y-0.5 overflow-y-auto px-3 py-4", children: visibleSections.map((section, i) => {
                            // Un-headed sections (the top daily-driver cluster) are always
                            // visible — never collapse the most-used links behind a chevron.
                            if (!section.heading) {
                                return (_jsx("div", { className: i === 0 ? '' : 'mt-2', children: renderItems(section.items) }, i));
                            }
                            // Headed sections are collapsible. Force-open when they contain the
                            // active route so the current page is always reachable.
                            const open = groupIsActive(section.items) || !!expandedGroups[section.heading];
                            return (_jsxs("div", { className: "mt-4", children: [_jsxs("button", { type: "button", "aria-expanded": open, onClick: () => toggleGroup(section.heading), className: "flex w-full items-center justify-between rounded-md px-3 py-1.5 text-meta text-styx-300 transition-colors hover:text-styx-100", children: [_jsx("span", { children: t(section.heading) }), _jsx(ChevronDown, { size: 14, className: cn('transition-transform', open ? '' : '-rotate-90') })] }), open && _jsx("div", { className: "mt-0.5 space-y-0.5", children: renderItems(section.items) })] }, i));
                        }) }), _jsxs("div", { className: "border-t border-styx-800 p-3", children: [_jsxs("div", { className: "flex items-center gap-3 rounded-lg bg-styx-800/60 p-2.5", children: [_jsx(Avatar, { name: meData.user.display_name }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: "truncate text-sm font-medium text-white", children: meData.user.display_name }), _jsx("div", { className: "truncate text-[11px] text-styx-300", children: meData.user.email })] })] }), _jsxs("div", { className: "mt-2 flex flex-wrap gap-1 px-1", children: [_jsx("span", { className: cn('rounded-md px-1.5 py-0.5 text-tag', badge.tone), children: t(badge.label) }), meData.roles.map((r) => (_jsx("span", { className: "rounded-md bg-styx-800 px-1.5 py-0.5 text-[10px] font-medium text-styx-200", children: r.name }, r.id)))] }), meData.feature_flags?.[MULTI_LANGUAGE_FLAG] && (_jsxs("div", { className: "mt-3 flex items-center gap-2 px-1", children: [_jsx(Languages, { size: 14, className: "shrink-0 text-styx-300" }), _jsx("select", { "aria-label": t('Language'), value: i18n.language, onChange: (e) => {
                                            const code = e.target.value;
                                            void applyLocale(code);
                                            api.patch('/me', { locale: code }).catch(() => {
                                                /* UI already switched; the save retries on next change */
                                            });
                                        }, className: "w-full rounded-md border border-styx-700 bg-styx-800/60 px-2 py-1 text-xs text-styx-100 focus:outline-none", children: LANGUAGES.map((l) => (_jsx("option", { value: l.code, children: l.nativeName }, l.code))) })] })), _jsxs(Button, { size: "sm", variant: "ghost", className: "mt-3 w-full justify-start text-styx-200 hover:bg-styx-800/70 hover:text-white", onClick: () => { logout(); navigate('/login'); }, children: [_jsx(LogOut, { size: 14 }), t('Sign out')] }), _jsxs(Button, { size: "sm", variant: "ghost", className: "mt-1 w-full justify-start text-styx-300 hover:bg-styx-800/70 hover:text-white", onClick: () => setConfirmSignOutAll(true), children: [_jsx(LogOut, { size: 14 }), t('Sign out everywhere')] })] })] }), _jsxs("main", { className: "flex flex-1 flex-col overflow-hidden", children: [_jsx(OfflineIndicator, {}), meData.impersonated_by && _jsx(ImpersonationBanner, { name: meData.user.display_name }), _jsxs("header", { className: "flex h-12 shrink-0 items-center justify-end gap-2 border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-900", children: [_jsx("button", { type: "button", "aria-label": "Open menu", onClick: () => setSidebarOpen(true), className: "mr-auto rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 md:hidden", children: _jsx(Menu, { size: 18 }) }), _jsx(SessionIndicator, {}), _jsx(RefreshButton, {}), _jsx(ThemeToggle, {}), _jsx(NotificationBell, {})] }), _jsx("div", { className: "flex-1 overflow-y-auto", children: _jsx("div", { className: "animate-fade-in", children: _jsx(Outlet, {}) }, i18n.language) })] }), _jsx(Modal, { open: confirmSignOutAll, onClose: () => !signingOutAll && setConfirmSignOutAll(false), title: "Sign out of every device?", children: _jsxs("div", { className: "space-y-4 text-sm", children: [_jsx("p", { className: "text-slate-600 dark:text-slate-300", children: "This will revoke every current sign-in for your account \u2014 this tab, every other tab, your phone, anyone signed in as you. They'll all land on the sign-in page next time they make any request." }), _jsx("p", { className: "text-slate-500 dark:text-slate-400", children: "Use this if you've lost a device, suspect someone else got your password, or just want to start fresh." }), _jsxs("div", { className: "flex justify-end gap-2 pt-2", children: [_jsx(Button, { variant: "ghost", onClick: () => setConfirmSignOutAll(false), disabled: signingOutAll, children: "Cancel" }), _jsx(Button, { variant: "danger", disabled: signingOutAll, onClick: async () => {
                                        setSigningOutAll(true);
                                        await signOutEverywhere();
                                        // signOutEverywhere triggers MSAL logoutRedirect, which
                                        // navigates away — we won't reach the navigate() below
                                        // in MSAL mode. In dev mode (X-Dev-User), there's no
                                        // redirect, so we must do it ourselves.
                                        navigate('/login?reason=signed_out_everywhere');
                                    }, children: signingOutAll ? 'Signing out…' : 'Sign out everywhere' })] })] }) })] }));
}
function Avatar({ name }) {
    const initials = name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
    const hues = ['from-brand-500 to-purple-500', 'from-cyan-500 to-blue-500', 'from-emerald-500 to-teal-500', 'from-rose-500 to-pink-500', 'from-amber-500 to-orange-500'];
    const hue = hues[name.length % hues.length];
    return (_jsx("div", { className: cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-bold text-white', hue), children: initials }));
}
export function PageHeader({ title, description, actions, }) {
    return (_jsx("div", { className: "border-b border-slate-200 bg-white px-8 py-6 dark:border-slate-800 dark:bg-slate-900", children: _jsxs("div", { className: "flex flex-wrap items-end justify-between gap-4", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50", children: title }), description && _jsx("p", { className: "mt-1 text-sm text-slate-500", children: description })] }), actions && _jsx("div", { className: "flex gap-2", children: actions })] }) }));
}
export function PageBody({ children }) {
    return _jsx("div", { className: "p-8", children: children });
}
/**
 * Super-Admin "View as" banner. Always visible while impersonating so the
 * admin can never forget they're acting as someone else, and can exit in one
 * click. Backend audits both start + stop.
 */
function ImpersonationBanner({ name }) {
    return (_jsxs("div", { className: "flex shrink-0 items-center justify-between gap-3 bg-amber-500 px-4 py-1.5 text-sm font-medium text-amber-950", children: [_jsxs("span", { children: ["\uD83D\uDC41 Viewing as ", _jsx("b", { children: name }), " \u2014 you are acting as this user. All actions are recorded."] }), _jsx("button", { type: "button", onClick: () => void stopImpersonation(), className: "rounded-md bg-amber-950/20 px-3 py-1 text-xs font-semibold text-amber-950 hover:bg-amber-950/30", children: "Exit \u201CView as\u201D" })] }));
}
/**
 * RefreshButton — global "refresh all visible data" button in the AppShell
 * top bar (2026-06-23).
 *
 * Why: every authenticated page in the app fetches data via TanStack
 * Query. Users were having to hard-refresh the browser to pick up new
 * tickets / status changes / dispatch updates, which loses scroll
 * position + form drafts. One click here re-runs every currently-
 * mounted React Query. Unmounted queries stay untouched (TanStack
 * is smart — it only refetches "active" queries by default), so this
 * isn't a "refetch the entire app" hammer.
 *
 * UX:
 *  - Spinning animation on the icon during the refetch wave.
 *  - Disabled while in-flight so a fast double-click doesn't queue
 *    two refetch waves.
 *  - Native `title` tooltip explains the keyboard / mouse affordance.
 *  - The browser's hard-refresh still works as before; this button is
 *    the SOFT-refresh that preserves SPA state.
 */
function RefreshButton() {
    const qc = useQueryClient();
    const [busy, setBusy] = useState(false);
    const onClick = async () => {
        if (busy)
            return;
        setBusy(true);
        try {
            // invalidateQueries() refetches every ACTIVE (mounted) query;
            // returns when the refetch wave settles. Stale queries on
            // unmounted pages just get marked stale and refetch on next mount.
            await qc.invalidateQueries();
        }
        finally {
            // Small floor so the spinner is visible even on fast networks;
            // gives the user feedback the click landed.
            setTimeout(() => setBusy(false), 400);
        }
    };
    return (_jsx("button", { type: "button", onClick: onClick, disabled: busy, "aria-label": "Refresh visible data", title: "Refresh \u2014 re-fetch what's on screen without losing your scroll position", className: "rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200", children: _jsx(RefreshCw, { size: 16, className: busy ? 'animate-spin' : undefined }) }));
}
